// Share File Modal - Modern UI for sharing individual files
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, Copy, Check, Calendar, Download, Eye, 
  Key, Shield, ExternalLink, Share2, Sparkles 
} from 'lucide-react';
import { AudioFile } from '@types';
import { useToast } from '@components/ui';
import { cn } from '@utils/classNames';
import { fileSharingService, FileShare } from '@services/file-sharing.service';
import { showSuspensionToastIfNeeded, SUSPENSION_ERROR_MESSAGE } from '@utils/suspension';
import { useFileStore } from '@stores/useFileStore';

interface ShareFileModalProps {
  file: AudioFile;
  onClose: () => void;
}

export const ShareFileModal: React.FC<ShareFileModalProps> = ({ file, onClose }) => {
  const { success, error: showError } = useToast();
  const [shareLink, setShareLink] = useState<string>('');
  const [existingShare, setExistingShare] = useState<FileShare | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Share options
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(30);
  const [maxDownloads, setMaxDownloads] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Contact overrides for this share (optional; overrides profile defaults)
  const [contactEmailOverride, setContactEmailOverride] = useState('');
  const [instagramUrlOverride, setInstagramUrlOverride] = useState('');
  const [beatstarsProfileUrlOverride, setBeatstarsProfileUrlOverride] = useState('');
  // Buy link at share level (so user can add link when sharing even if file has none)
  const [beatstarsSellUrl, setBeatstarsSellUrl] = useState('');
  const [beatstarsPrice, setBeatstarsPrice] = useState('');
  const [showBuyButton, setShowBuyButton] = useState(true);
  const [showContact, setShowContact] = useState(true);

  // Load existing share on mount
  useEffect(() => {
    loadExistingShare();
  }, [file.id]);

  const loadExistingShare = async () => {
    try {
      const share = await fileSharingService.getFileShare(file.id);
      if (share) {
        setExistingShare(share);
        setShareLink(`${window.location.origin}/share/file/${share.share_token}`);
        setHasPassword(!!share.password);
        if (share.expires_at) {
          const expiresDate = new Date(share.expires_at);
          const now = new Date();
          const days = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          setExpiresInDays(days > 0 ? days : null);
        } else {
          setExpiresInDays(null);
        }
        setMaxDownloads(share.max_downloads);
        setContactEmailOverride((share as any).contact_email_override ?? '');
        setInstagramUrlOverride((share as any).instagram_url_override ?? '');
        setBeatstarsProfileUrlOverride((share as any).beatstars_profile_url_override ?? '');
        setBeatstarsSellUrl(share.beatstars_sell_url ?? file.beatstarsSellUrl ?? '');
        setBeatstarsPrice(share.beatstars_price ?? file.beatstarsPrice ?? '');
        setShowBuyButton(share.show_buy_button ?? true);
        setShowContact(share.show_contact ?? true);
      } else {
        setBeatstarsSellUrl(file.beatstarsSellUrl ?? '');
        setBeatstarsPrice(file.beatstarsPrice ?? '');
      }
    } catch (err) {
      console.error('Failed to load existing share:', err);
    }
  };

  const handleCreateShare = async () => {
    if (isCreating) return;
    
    // Check suspension FIRST before any state changes
    const isSuspended = await showSuspensionToastIfNeeded(showError);
    if (isSuspended) {
      console.log('🚫 [ShareFileModal] User is suspended - blocking share creation');
      return;
    }
    
    setIsCreating(true);
    try {
      const result = await fileSharingService.createFileShare({
        fileId: file.id,
        password: hasPassword ? password : undefined,
        expiresInDays: expiresInDays,
        maxDownloads: maxDownloads || undefined,
        contactEmailOverride: contactEmailOverride.trim() || null,
        instagramUrlOverride: instagramUrlOverride.trim() || null,
        beatstarsProfileUrlOverride: beatstarsProfileUrlOverride.trim() || null,
        beatstarsSellUrl: beatstarsSellUrl.trim() || null,
        beatstarsPrice: beatstarsPrice.trim() || null,
        showBuyButton,
        showContact,
      });

      // Optimistically update the file in the store to immediately reflect the change
      // Since isShared is computed, we update it directly in the store state
      const { files } = useFileStore.getState();
      const updatedFiles = files.map((f) => 
        f.id === file.id ? { ...f, isShared: true } : f
      );
      useFileStore.setState({ files: updatedFiles });
      setShareLink(result.shareUrl);
      setExistingShare(result.share);
      success('Share created!', 'Your file is now ready to share.');
      // Don't close the dialog - let user see the link and copy it
    } catch (err: any) {
      console.error('Failed to create share:', err);
      if (err.message === 'ACCOUNT_SUSPENDED') {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
      } else {
        showError('Failed to create share', err.message || 'Please try again.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) {
      showError('No link available', 'Please create a share link first.');
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      success('Link copied!', 'The share link has been copied to your clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showError('Failed to copy', 'Please try again.');
    }
  };

  const handleDeactivateShare = async () => {
    try {
      // Check if account is manually suspended (without download access)
      const isSuspended = await showSuspensionToastIfNeeded(showError);
      if (isSuspended) {
        return;
      }
      
      await fileSharingService.deactivateFileShare(file.id);
      // Optimistically update the file in the store to immediately reflect the change
      // Since isShared is computed, we update it directly in the store state
      const { files } = useFileStore.getState();
      const updatedFiles = files.map((f) => 
        f.id === file.id ? { ...f, isShared: false } : f
      );
      useFileStore.setState({ files: updatedFiles });
      setExistingShare(null);
      setShareLink('');
      success('Share deactivated', 'The share link is no longer active.');
      // Don't close the dialog automatically - user can close it manually if needed
    } catch (err: any) {
      if (err.message === 'ACCOUNT_SUSPENDED') {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
      } else {
        showError('Failed to deactivate', err.message || 'Please try again.');
      }
    }
  };

  const handleOpenShare = () => {
    if (shareLink) {
      window.open(shareLink, '_blank');
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-secondary/95 border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg animate-slide-up overflow-hidden">
        {/* Header with gradient accent */}
        <div className="relative bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border-b border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center border border-primary/20">
                <Share2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-dela text-white">Share File</h2>
                <p className="text-sm text-white/60 font-inter mt-0.5">Create a shareable link</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* File Info Card */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-inter font-semibold truncate">{file.name}</p>
                <p className="text-xs text-white/50 font-inter">
                  {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type.toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Share Link Section */}
          {shareLink ? (
            <div className="space-y-3">
              <label className="block text-sm font-inter font-semibold text-white/90">
                Share Link
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <button
                  onClick={handleCopyLink}
                  className={cn(
                    "px-5 py-3 rounded-xl transition-all font-inter font-bold text-sm flex items-center gap-2 min-w-[100px] justify-center",
                    copied
                      ? "bg-primary text-black shadow-lg shadow-primary/30"
                      : "bg-white/10 hover:bg-white/20 text-white"
                  )}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={handleOpenShare}
                  className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
              
              {/* Share Stats */}
              {existingShare && (
                <div className="flex items-center gap-4 text-xs text-white/60 font-inter pt-2">
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    <span>{existingShare.views} views</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" />
                    <span>{existingShare.download_count} downloads</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
              <p className="text-white/60 font-inter text-sm mb-4">
                Create a share link to start sharing this file
              </p>
            </div>
          )}

          {/* Advanced Options Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all border border-white/10"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-white/60" />
              <span className="text-sm font-inter font-semibold text-white">Advanced Options</span>
            </div>
            <span className={cn(
              "text-xs text-white/40 transition-transform",
              showAdvanced && "rotate-180"
            )}>▼</span>
          </button>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 bg-white/5 rounded-xl p-4 border border-white/10 animate-fade-in">
              {/* Password Protection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm font-inter font-semibold text-white">
                    <Key className="w-4 h-4 text-primary" />
                    Password Protection
                  </label>
                  <button
                    onClick={() => {
                      setHasPassword(!hasPassword);
                      if (!hasPassword) setPassword('');
                    }}
                    className={cn(
                      "relative w-11 h-6 rounded-full transition-all",
                      hasPassword ? "bg-primary" : "bg-white/20"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-all",
                        hasPassword && "translate-x-5"
                      )}
                    />
                  </button>
                </div>
                {hasPassword && (
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                )}
              </div>

              {/* Expiration */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-inter font-semibold text-white">
                  <Calendar className="w-4 h-4 text-primary" />
                  Link Expiration
                </label>
                <select
                  value={expiresInDays === null ? 'never' : expiresInDays.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setExpiresInDays(value === 'never' ? null : parseInt(value));
                  }}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="1">1 day</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="never">Never expires</option>
                </select>
              </div>

              {/* Max Downloads */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-inter font-semibold text-white">
                  <Download className="w-4 h-4 text-primary" />
                  Max Downloads
                </label>
                <input
                  type="number"
                  value={maxDownloads || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMaxDownloads(value === '' ? null : parseInt(value));
                  }}
                  placeholder="Unlimited"
                  min="1"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-xs text-white/50 font-inter">
                  Leave empty for unlimited downloads
                </p>
              </div>

              {/* Contact overrides for this share */}
              <div className="space-y-3 pt-2 border-t border-white/10">
                <label className="block text-sm font-inter font-semibold text-white">
                  Contact on share page (optional overrides)
                </label>
                <p className="text-xs text-white/50 font-inter">
                  Override your default contact for this share only. Leave empty to use profile defaults.
                </p>
                <input
                  type="email"
                  value={contactEmailOverride}
                  onChange={(e) => setContactEmailOverride(e.target.value)}
                  placeholder="Contact email"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <input
                  type="url"
                  value={instagramUrlOverride}
                  onChange={(e) => setInstagramUrlOverride(e.target.value)}
                  placeholder="Instagram profile URL"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <input
                  type="url"
                  value={beatstarsProfileUrlOverride}
                  onChange={(e) => setBeatstarsProfileUrlOverride(e.target.value)}
                  placeholder="BeatStars profile URL"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Buy link (optional – add link when sharing if file has none) */}
              <div className="space-y-3 pt-2 border-t border-white/10">
                <label className="block text-sm font-inter font-semibold text-white">
                  Buy link (optional)
                </label>
                <p className="text-xs text-white/50 font-inter">
                  BeatStars sell URL and price for this share. Shown as a buy button on the share page.
                </p>
                <input
                  type="url"
                  value={beatstarsSellUrl}
                  onChange={(e) => setBeatstarsSellUrl(e.target.value)}
                  placeholder="BeatStars sell URL"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <input
                  type="text"
                  value={beatstarsPrice}
                  onChange={(e) => setBeatstarsPrice(e.target.value)}
                  placeholder="Price (e.g. 49 € or $29)"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-inter text-white">Show buy button on share page</span>
                  <button
                    type="button"
                    onClick={() => setShowBuyButton(!showBuyButton)}
                    className={cn(
                      "relative w-11 h-6 rounded-full transition-all",
                      showBuyButton ? "bg-primary" : "bg-white/20"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-all",
                        showBuyButton && "translate-x-5"
                      )}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-inter text-white">Show contact on share page</span>
                  <button
                    type="button"
                    onClick={() => setShowContact(!showContact)}
                    className={cn(
                      "relative w-11 h-6 rounded-full transition-all",
                      showContact ? "bg-primary" : "bg-white/20"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-all",
                        showContact && "translate-x-5"
                      )}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            {existingShare ? (
              <>
                <button
                  onClick={handleCreateShare}
                  disabled={isCreating}
                  className="flex-1 px-4 py-3 bg-primary hover:bg-primary/90 text-black rounded-xl transition-all font-inter font-bold text-sm shadow-lg shadow-primary/20 disabled:opacity-50"
                >
                  {isCreating ? 'Updating...' : 'Update Share'}
                </button>
                <button
                  onClick={handleDeactivateShare}
                  className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all font-inter font-semibold text-sm"
                >
                  Deactivate
                </button>
              </>
            ) : (
              <button
                onClick={handleCreateShare}
                disabled={isCreating}
                className="w-full px-4 py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-black rounded-xl transition-all font-inter font-bold text-sm shadow-lg shadow-primary/20 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Create Share Link
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
